/**
 * Any consts/preferences in terms of React-Query.
 */


/**
 * 5 min "fresh"
 */
export const queryClientStaleTime = 1000 * 60 * 5;

/**
 * 1 hour cache
 */
export const queryClientCacheTime = 1000 * 60 * 60;

/**
 * Maximum time that the storage (in session) should stay.
 * 60 minutes = 1000 * 60 * 60 * 24 (24 hours).
 */
export const maxAgePersistStorage = 1000 * 60 * 60 * 24;

const MINUTE = 1000 * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

export const QUERY_TIMES = Object.freeze({
    INSTANT: Object.freeze({
        staleTime: 0,
        cacheTime: MINUTE * 5
    }),
    VERY_SHORT: Object.freeze({
        staleTime: MINUTE,
        cacheTime: MINUTE * 15
    }),
    SHORT: Object.freeze({
        staleTime: MINUTE * 2,
        cacheTime: MINUTE * 30
    }),
    MEDIUM: Object.freeze({
        staleTime: MINUTE * 5,
        cacheTime: HOUR
    }),
    LONG: Object.freeze({
        staleTime: MINUTE * 30,
        cacheTime: HOUR * 2
    }),
    VERY_LONG: Object.freeze({
        staleTime: HOUR,
        cacheTime: DAY
    }),
    STATIC: Object.freeze({
        staleTime: DAY,
        cacheTime: DAY * 7
    })
});

/**
 * Frequently used React Query cache profiles.
 * These profiles provide sensible stale/cache pairings for time. Add more if needed.
 */
export const QUERY_CACHE_PROFILES = Object.freeze({
    /**
     * Default profile - NOT PERSISTED
     * Suitable for most API calls. Short enough.
     */
    DEFAULT: Object.freeze({
        ...QUERY_TIMES.VERY_SHORT,
        persist: false
    }),

    /**
     * Frequently changing lightweight data - PERSISTED
     * e.g. dashboard counts, indicators or badges.
     */
    LIVE: Object.freeze({
        ...QUERY_TIMES.LONG,
        persist: true
    }),

    /**
     * Lookup/reference data that changes very rarely - PERSISTED
     * e.g. Schools, campuses, lecturers, dropdown values, etc.
     */
    LOOKUP: Object.freeze({
        ...QUERY_TIMES.VERY_LONG,
        persist: true
    }),

    /**
     * Search results and paginated tables - NOT PERSISTED
     * e.g. Paginated or searched tables/lists
     */
    TABLE: Object.freeze({
        ...QUERY_TIMES.SHORT,
        persist: false
    }),

    /**
     * Individual entities or configuration pages - PERSISTED
     * e.g. User profile, settings, details, etc.
     */
    DETAILS: Object.freeze({
        ...QUERY_TIMES.LONG,
        persist: true
    })
});

/**
 * Keys that get cached by React Queries.
 * 1. Invalidate by root (e.g. "dashboard.root" will invalidate everything under "dashboard").
 * 2. Invalidate individually (e.g. "dashboard.allCounts" will only invalidate allCounts).
 * If it's invalidated, it will get refetched.
 */
export const QUERY_KEYS = {
    dashboard: {
        root: "dashboard",
        allCounts: "allCounts",
        allLecturers: "allLecturers",
        commonContentReReg: "commonContentReReg",
        mentoredStudents: "mentoredStudents",
        unreadCourseNotifications: "unreadCourseNotifications"
    },
    courseAdministration: {
        root: "courseAdministration",
        courseDropdown: "courseDropdown",
        courseList: "courseList"
    }
};
